export type GameId =
  | "impostor"
  | "mafia"
  | "secret-hitler"
  | "alibi"
  | "liar-dice"
  | "spectrum";
export type GameInfo = {
  id: GameId;
  name: string;
  players: string;
  min: number;
  max: number;
  minutes: string;
  category: string;
  steps: string[];
  detail: string;
  defaults: Record<string, unknown>;
  /** Guided-pace timers this game actually uses (mirrors engine metadata `timerKinds`). */
  timers: TimerKind[];
};
export type TimerKind = "clue" | "decision" | "turn" | "discussion";
export const games: GameInfo[] = [
  {
    id: "impostor",
    timers: ["clue", "discussion", "decision"],
    name: "Impostor",
    players: "3–12",
    min: 3,
    max: 12,
    minutes: "5–10",
    category: "deduction",
    steps: [
      "Check your secret card. Most players share a word; one person is the impostor.",
      "Take turns giving a short clue without using your word.",
      "Discuss, then secretly vote. Catch the impostor—but they can still win by guessing the word.",
    ],
    detail:
      "A tied ballot gets one runoff. A second tie, or accusing a local, lets the impostor win. In Related words mode the impostor gets a different, related word and knows their role.",
    defaults: {
      variant: "blank",
      packId: "everyday",
      cluePasses: 1,
      finalGuess: true,
    },
  },
  {
    id: "mafia",
    timers: ["decision", "discussion"],
    name: "Mafia",
    players: "6–12",
    min: 6,
    max: 12,
    minutes: "15–30",
    category: "deduction",
    steps: [
      "Read your role privately. Town must find the mafia; mafia must reach parity with town.",
      "At night, mafia choose a target, the doctor protects and the detective investigates. Villagers take no action.",
      "Discuss the morning’s news. Secretly vote to eliminate someone; more than half of all living players must agree.",
    ],
    detail:
      "No majority means no elimination. The doctor cannot protect the same person on consecutive nights by default. Dead players watch silently and keep their previous private knowledge. Roles remain hidden until the end unless configured otherwise.",
    defaults: {
      mafiaCount: "auto",
      detective: true,
      doctor: true,
      doctorSelfSave: true,
      doctorRepeatSave: false,
      revealEliminatedRoles: false,
    },
  },
  {
    id: "secret-hitler",
    timers: ["turn", "decision"],
    name: "Secret Hitler",
    players: "5–10",
    min: 5,
    max: 10,
    minutes: "30–50",
    category: "deduction",
    steps: [
      "Read your secret political role. Liberals seek five liberal policies; fascists seek six fascist policies or Hitler’s election as chancellor after three fascist policies.",
      "The president nominates a chancellor. Everyone votes on the proposed government; a strict majority is needed.",
      "An elected president draws three policies and discards one. The chancellor enacts one of the remaining two. Fascist policies may unlock presidential powers.",
    ],
    detail:
      "Three failed elections enact the next policy without an executive power. After five fascist policies, both elected officials must agree to veto. Killing Hitler wins for liberals.",
    defaults: {},
  },
  {
    id: "alibi",
    timers: ["turn", "discussion", "decision"],
    name: "Alibi",
    players: "3–12",
    min: 3,
    max: 12,
    minutes: "6–10",
    category: "deduction",
    steps: [
      "Everyone sees a list of possible places. Locals privately learn the real location; the spy does not.",
      "Take turns asking one another questions. By default, ask and answer aloud, then tap to move on—no typing needed.",
      "Discuss and secretly vote for the spy. The spy can risk a location guess, including one final guess if caught.",
    ],
    detail:
      "Ask questions that establish you belong without naming the place. A wrong accusation or second tied vote lets the spy escape. Turn on Text conversation for silent play, or create a custom location pack.",
    defaults: {
      packId: "city-after-dark",
      questionRounds: 1,
      candidateCount: 8,
      conversationMode: "spoken",
      spyEarlyGuess: true,
    },
  },
  {
    id: "liar-dice",
    timers: ["turn"],
    name: "Liar’s Dice",
    players: "2–8",
    min: 2,
    max: 8,
    minutes: "10–20",
    category: "quick",
    steps: [
      "Peek at your own dice. Everyone’s dice stay hidden.",
      "On your turn, raise the bid: guess how many dice of a face exist across the whole table. Raise the quantity, or keep the quantity and raise the face.",
      "Or challenge the last bid. Everyone reveals; the bidder loses a die if the bid was too high, otherwise the challenger does. Last player with dice wins.",
    ],
    detail:
      "With wild ones on, ones count towards any face, but you may only bid faces 2–6. With wild ones off, only the exact face counts. There are no special half/double bid conversions.",
    defaults: { startingDice: 5, onesWild: true },
  },
  {
    id: "spectrum",
    timers: ["clue", "discussion"],
    name: "Spectrum",
    players: "2–12",
    min: 2,
    max: 12,
    minutes: "10–20",
    category: "quick",
    steps: [
      "The guide sees a secret target on a scale between two opposites.",
      "The guide gives a clue for that position. Other players discuss and make private guesses; the guide stays silent.",
      "Reveal the median guess and score up to four points for accuracy. A new guide takes over next round.",
    ],
    detail:
      "Everyone plays on the same team. The median of submitted guesses is the team’s answer. Score 4 points within 3, 3 within 8, 2 within 15, or 1 within 25. By default everyone gets one turn guiding.",
    defaults: {
      rounds: "everyone",
      packId: "everyday-judgments",
      targetMode: "full-scale",
    },
  },
];
export const gameById = (id: string) =>
  games.find((g) => g.id === id) ?? games[0];
